import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  FileImage,
  ShieldCheck,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useBankSlipUpload } from '../../application/hooks/usePayments';
import { useMySubscription } from '../../application/hooks/useSubscription';
import { BookingLockNotice } from '../components/payments/BookingLockNotice';
import { useTranslation } from '../../application/hooks/useTranslation';
import { formatBDT } from '../../domain/formatters/currency';
import { errorMessageOf } from '../components/feedback/QueryState';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import './pages.css';
import './BankSlip.css';
import { todayLocal } from '../../domain/localDate';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const POPULAR_BANKS = [
  'Sonali Bank PLC',
  'The City Bank Ltd',
  'Dutch-Bangla Bank',
  'Islami Bank Bangladesh',
  'BRAC Bank PLC',
  'Prime Bank Ltd',
];

/**
 * Client-side gate: type + ≤5 MB; if oversized, try canvas downscale/re-encode to JPEG.
 */
async function tryCompress(file: File): Promise<File | null> {
  try {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decode failed'));
        el.src = url;
      });
      const scale = Math.min(1, 1920 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.8),
      );
      if (!blob || blob.size > MAX_BYTES) return null;
      return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return null;
  }
}

interface DetailsForm {
  bankName: string;
  branchName: string;
  scrollId: string;
  depositDate: string;
  amount: string;
}

type Field = keyof DetailsForm;

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * Bank slip upload: (1) deposit details, (2) slip photo, (3) review and submit, then a
 * pending-verification confirmation. An administrator verifies the slip before it is credited.
 */
export default function BankSlipUploadPage() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const subscription = useMySubscription();
  const upload = useBankSlipUpload();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const [step, setStep] = useState(1);
  const [details, setDetails] = useState<DetailsForm>({
    bankName: '',
    branchName: '',
    scrollId: '',
    depositDate: todayLocal(),
    amount: '',
  });
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const today = todayLocal();
  const amountNum = Number(details.amount);

  const errors: Partial<Record<Field, string>> = {
    ...(details.bankName.trim().length > 1 ? {} : { bankName: t('payPolish.bankInvalid') }),
    ...(details.branchName.trim().length > 1 ? {} : { branchName: t('payPolish.branchInvalid') }),
    ...(details.scrollId.trim().length > 0 ? {} : { scrollId: t('payPolish.scrollInvalid') }),
    ...(/^\d{4}-\d{2}-\d{2}$/.test(details.depositDate) && details.depositDate <= today
      ? {}
      : { depositDate: t('payPolish.dateInvalid') }),
    ...(Number.isFinite(amountNum) && amountNum > 0 ? {} : { amount: t('payPolish.amountInvalid') }),
  };
  const detailsValid = Object.keys(errors).length === 0;
  const shown = (f: Field) => (touched[f] ? errors[f] : undefined);
  const touch = (f: Field) => () => setTouched((s) => ({ ...s, [f]: true }));

  // Move focus to the step heading so screen readers and keyboard users land on the new content.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const handleFile = async (picked: File | null) => {
    setFileError(null);
    if (!picked) return;
    if (!ALLOWED_TYPES.includes(picked.type)) {
      setFileError(t('bankSlip.badType'));
      return;
    }
    let next = picked;
    if (picked.size > MAX_BYTES) {
      setCompressing(true);
      next = (await tryCompress(picked)) ?? picked;
      setCompressing(false);
      if (next.size > MAX_BYTES) {
        setFileError(t('bankSlip.tooBig'));
        return;
      }
    }
    if (preview && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(preview);
    setFile(next);
    if (typeof URL.createObjectURL === 'function') {
      setPreview(URL.createObjectURL(next));
    }
  };

  const removeFile = () => {
    if (preview && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setFileError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = () => {
    if (!subscription.data || !file) return;
    setFormError(null);
    upload.mutate(
      {
        fields: {
          subscriptionId: subscription.data.id,
          bankName: details.bankName.trim(),
          branchName: details.branchName.trim(),
          scrollId: details.scrollId.trim(),
          depositDate: details.depositDate,
          amount: Number(details.amount),
        },
        file,
      },
      {
        onSuccess: () => setStep(4),
        onError: (e: unknown) => setFormError(errorMessageOf(e, t('common.error'), t)),
      },
    );
  };

  const stepNode = (n: 1 | 2 | 3, label: string) => (
    <li
      className={`bsp-step${step === n ? ' is-active' : ''}${step > n ? ' is-done' : ''}`}
      aria-current={step === n ? 'step' : undefined}
    >
      <span className="bsp-step-num" aria-hidden>
        {step > n ? <Check size={13} /> : n}
      </span>
      <span>{label}</span>
    </li>
  );

  const sub = subscription.data;
  if (sub?.bookingVerified === false) return <BookingLockNotice />;

  return (
    <div className="bsp-page" data-testid="bankslip-page">
      <header className="bsp-head">
        <h1 className="bsp-title">{t('payPolish.slipTitle')}</h1>
        <p className="muted">{t('payPolish.slipSubtitle')}</p>
      </header>

      {subscription.isNoSubscription && (
        <div className="bsp-alert" role="alert" data-testid="bs-no-subscription-alert">
          <AlertTriangle size={20} aria-hidden />
          <div>
            <strong>{t('bankSlip.noSubscriptionTitle')}</strong>
            <p>{t('bankSlip.noSubscriptionDesc')}</p>
          </div>
        </div>
      )}

      {sub && step < 4 && (
        <div className="bsp-summary" aria-label={t('payPolish.summaryTitle')}>
          <div className="bsp-summary-item">
            <span className="bsp-eyebrow">{t('payPolish.summaryEmi')}</span>
            <strong>{formatBDT(sub.monthlyAmount, locale)}</strong>
          </div>
          <div className="bsp-summary-item">
            <span className="bsp-eyebrow">{t('payPolish.summaryCleared')}</span>
            <strong>
              {sub.clearedInstallments} / {sub.totalInstallments}
            </strong>
          </div>
        </div>
      )}

      {step < 4 && (
        <ol className="bsp-stepper" aria-label={t('payPolish.slipTitle')}>
          {stepNode(1, t('payPolish.stepDetails'))}
          {stepNode(2, t('payPolish.stepSlip'))}
          {stepNode(3, t('payPolish.stepReview'))}
        </ol>
      )}

      {step === 4 && (
        <Card>
          <div className="bsp-success" data-testid="bankslip-success">
            <span className="bsp-success-icon" aria-hidden>
              <CheckCircle2 size={34} />
            </span>
            <Badge tone="warning">{t('payPolish.pendingVerification')}</Badge>
            <h2 className="bsp-heading" ref={headingRef} tabIndex={-1}>
              {t('payPolish.successTitle')}
            </h2>
            <p className="muted">{t('payPolish.successBody')}</p>
          </div>

          <dl className="kv bsp-recap">
            <dt>{t('bankSlip.bank')}</dt>
            <dd>{details.bankName}</dd>
            <dt>{t('payPolish.summaryDeposit')}</dt>
            <dd>{formatBDT(amountNum, locale)}</dd>
            <dt>{t('bankSlip.depositDate')}</dt>
            <dd>{details.depositDate}</dd>
          </dl>

          <ul className="bsp-next">
            {(['next1', 'next2', 'next3'] as const).map((k) => (
              <li key={k}>
                <ShieldCheck size={15} aria-hidden />
                <span>{t(`payPolish.${k}`)}</span>
              </li>
            ))}
          </ul>

          <div className="bsp-actions">
            <Button onClick={() => navigate('/payments')} data-testid="bs-to-history">
              {t('payPolish.toHistory')}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/')} data-testid="bs-to-dashboard">
              {t('payPolish.toDashboard')}
            </Button>
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <h2 className="bsp-heading" ref={headingRef} tabIndex={-1}>
            {t('payPolish.stepDetails')}
          </h2>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (detailsValid) setStep(2);
            }}
            noValidate
          >
            <div className="bsp-banks">
              <span className="bsp-eyebrow">{t('payPolish.quickBanks')}</span>
              <div className="bsp-chip-row">
                {POPULAR_BANKS.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={`bsp-chip${details.bankName === b ? ' is-on' : ''}`}
                    aria-pressed={details.bankName === b}
                    onClick={() => setDetails({ ...details, bankName: b })}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <Input
              id="bsp-bank"
              label={t('bankSlip.bank')}
              value={details.bankName}
              placeholder={t('payPolish.bankPlaceholder')}
              onChange={(e) => setDetails({ ...details, bankName: e.target.value })}
              onBlur={touch('bankName')}
              error={shown('bankName')}
              required
              data-testid="bs-bank"
            />
            <Input
              id="bsp-branch"
              label={t('bankSlip.branch')}
              value={details.branchName}
              placeholder={t('payPolish.branchPlaceholder')}
              onChange={(e) => setDetails({ ...details, branchName: e.target.value })}
              onBlur={touch('branchName')}
              error={shown('branchName')}
              required
              data-testid="bs-branch"
            />
            <Input
              id="bsp-scroll"
              label={t('bankSlip.scrollId')}
              value={details.scrollId}
              hint={t('payPolish.scrollHint')}
              onChange={(e) => setDetails({ ...details, scrollId: e.target.value })}
              onBlur={touch('scrollId')}
              error={shown('scrollId')}
              required
              data-testid="bs-scroll"
            />
            <div className="bsp-pair">
              <Input
                id="bsp-date"
                label={t('bankSlip.depositDate')}
                type="date"
                value={details.depositDate}
                max={today}
                onChange={(e) => setDetails({ ...details, depositDate: e.target.value })}
                onBlur={touch('depositDate')}
                error={shown('depositDate')}
                required
                data-testid="bs-date"
              />
              <Input
                id="bsp-amount"
                label={t('bankSlip.amount')}
                type="number"
                min={1}
                inputMode="decimal"
                placeholder={t('payPolish.amountPlaceholder')}
                value={details.amount}
                onChange={(e) => setDetails({ ...details, amount: e.target.value })}
                onBlur={touch('amount')}
                error={shown('amount')}
                required
                data-testid="bs-amount"
              />
            </div>
            <Button type="submit" className="bsp-cta" disabled={!detailsValid} data-testid="bs-next">
              <span>{t('bankSlip.next')}</span>
              <ArrowRight size={16} aria-hidden />
            </Button>
          </form>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <h2 className="bsp-heading" ref={headingRef} tabIndex={-1}>
            {t('bankSlip.pickFile')}
          </h2>
          <div className="stack">
            <button
              type="button"
              className={`bsp-drop${dragging ? ' is-drag' : ''}${file ? ' has-file' : ''}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                void handleFile(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              <span className="bsp-drop-icon" aria-hidden>
                <UploadCloud size={26} />
              </span>
              <strong>{dragging ? t('payPolish.dropDragTitle') : t('payPolish.dropTitle')}</strong>
              <span className="muted">{t('payPolish.dropHint')}</span>
            </button>

            <input
              id="slip-file"
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="bsp-file-input"
              onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
              data-testid="bs-file"
            />

            {compressing && (
              <p className="muted" role="status">
                {t('bankSlip.compressing')}
              </p>
            )}
            {fileError && (
              <p className="field-error" role="alert" data-testid="bs-file-error">
                {fileError}
              </p>
            )}

            {file && (
              <div className="bsp-file" data-testid="bs-file-card">
                {preview ? (
                  <img src={preview} alt={t('bankSlip.photo')} className="bsp-file-thumb" data-testid="bs-preview" />
                ) : (
                  <span className="bsp-file-thumb bsp-file-fallback" aria-hidden>
                    <FileImage size={22} />
                  </span>
                )}
                <div className="bsp-file-meta">
                  <strong className="bsp-file-name">{file.name}</strong>
                  <span className="muted">
                    {formatSize(file.size)} · {t('payPolish.fileReady')}
                  </span>
                  <div className="bsp-file-actions">
                    <button type="button" className="bsp-link" onClick={() => fileRef.current?.click()}>
                      {t('payPolish.replace')}
                    </button>
                    <button type="button" className="bsp-link is-danger" onClick={removeFile} data-testid="bs-remove">
                      <Trash2 size={13} aria-hidden />
                      {t('payPolish.remove')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bsp-actions">
              <Button variant="secondary" onClick={() => setStep(1)}>
                <ArrowLeft size={16} aria-hidden />
                <span>{t('bankSlip.prev')}</span>
              </Button>
              <Button disabled={!file || compressing} onClick={() => setStep(3)} data-testid="bs-next2">
                <span>{t('bankSlip.next')}</span>
                <ArrowRight size={16} aria-hidden />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <h2 className="bsp-heading" ref={headingRef} tabIndex={-1}>
            {t('payPolish.reviewTitle')}
          </h2>
          <p className="muted bsp-intro">{t('payPolish.reviewHint')}</p>

          <div className="bsp-amount-box">
            <span className="bsp-eyebrow">{t('payPolish.summaryDeposit')}</span>
            <strong className="bsp-amount">{formatBDT(amountNum, locale)}</strong>
          </div>

          <dl className="kv bsp-recap">
            <dt>{t('bankSlip.bank')}</dt>
            <dd>{details.bankName}</dd>
            <dt>{t('bankSlip.branch')}</dt>
            <dd>{details.branchName}</dd>
            <dt>{t('bankSlip.scrollId')}</dt>
            <dd className="bsp-mono">{details.scrollId}</dd>
            <dt>{t('bankSlip.depositDate')}</dt>
            <dd>{details.depositDate}</dd>
            <dt>{t('bankSlip.photo')}</dt>
            <dd className="bsp-break">{file?.name}</dd>
          </dl>

          <p className="bsp-note">
            <ShieldCheck size={16} aria-hidden />
            <span>{t('payPolish.hoursNote')}</span>
          </p>

          {formError && (
            <p className="text-danger" role="alert" data-testid="bs-submit-error">
              {formError}
            </p>
          )}

          <div className="bsp-actions">
            <Button variant="secondary" onClick={() => setStep(2)} disabled={upload.isPending}>
              <ArrowLeft size={16} aria-hidden />
              <span>{t('bankSlip.prev')}</span>
            </Button>
            <Button
              loading={upload.isPending}
              disabled={subscription.isNoSubscription}
              onClick={submit}
              data-testid="bs-submit"
            >
              <CheckCircle2 size={16} aria-hidden />
              <span>{t('bankSlip.submit')}</span>
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
